/* @flow */
import {
  RECEIVE_FOUNDRY_RESPONSE
} from '../actions/foundry_response';
import {
  ARTICLE_FULL_WIDTH_PAGE_TYPE,
  PRODUCT_DETAIL_PAGE_TYPE,
  PRODUCT_LIST_PAGE_TYPE,
  VIDEO_SERIES_PAGE_TYPE,
  SOLD_CONTENT_TYPES
} from '../constants';
import extract from '../utils/extract';
import sanitizeText from '../utils/sanitize_text';
import { standardAdPositionGenerator } from '../utils/ads/positionStrings';
import {
  isAssetSection, isImageSection,
  isPolyptychFromOldStoryEditor, isPolyptychFromNewStoryEditor,
  shouldShimAlignment, wasCreatedInOldStoryEditor
} from '../utils/sections';
import format from 'date-fns/format';
import {get, includes, flatMap, map, pick, last, reduce, isEmpty, isInteger, filter, isObject} from 'lodash';
import type { Action } from '../actions/types';
import sectionAds from '../utils/ads/section-ads';

const ARTICLE_PAGE_WIDTH_IMAGE_SHIM_THRESHOLD_PX = 1280;
const INITIAL_STATE = {};
const TIMESTAMP_FORMAT = 'YYYY-MM-DD HH:mm:ss';
const ARTICLE_ATTRIBUTES = [
  'asset_main.height',
  'asset_main.width',
  'asset_main.src_full',
  'asset_main.id',
  'asset_opener_article_full.src_full',
  'asset_opener_intro_slide_override.src_full',
  'asset_opener_intro_slide_override.credit',
  'asset_opener_intro_slide_override.width',
  'asset_opener_intro_slide_override.height',
  'asset_opener_intro_slide_override.impressiontracker',
  'asset_opener_intro_slide_override.clicktracker',
  'asset_opener_intro_slide_override.alt_title',
  ['breadcrumbs', [
    'name',
    'url'
  ]],
  ['shopping_breadcrumbs', [
    'name',
    'url'
  ]],
  'breaking_news',
  'bylines',
  'campaign_branding',
  'campaign_branding_image_mobile_url',
  'comments_id',
  'program_branding',
  'program_branding_image_url',
  'dek',
  'excerpt_truncated_325',
  'id',
  'is_video',
  ['opener_images', [
    'asset.credit',
    'asset.src.full',
    'asset.src.340x408',
    'alt_title',
    'label',
    'type'
  ]],
  'primary_tag',
  'original_published',
  'meta.ad_sponsor',
  'meta.full_intro_entry',
  'meta.product_layout',
  'meta.news_keywords',
  'meta.slideshow_full',
  'meta.is_full_width',
  'meta.meta_description',
  'meta.meta_title',
  'meta.social_title',
  'meta.campaign_branding_clicktracker',
  'meta.campaign_branding_impressiontracker',
  'meta.campaign_branding_nofollow',
  'meta.program_branding_click_tracker',
  'meta.program_branding_impression_tracker',
  'meta.program_branding_nofollow',
  'meta.sold_content_type',
  'meta.hide_product_info_button',
  'meta.rel_canonical_source',
  'newsletters',
  'primary_category',
  'program_branding_image_url',
  'published',
  'recommendations',
  'related_entries',
  'sections.body',
  'sections.slideshow',
  'simplereach',
  'title',
  'truncated_short_title_80',
  'type',
  'id',
  'url_full'
];

const getArticleOrInitialState =
  (state: ArticleState, action: Action): ArticleState =>
    includes([
      PRODUCT_LIST_PAGE_TYPE,
      PRODUCT_DETAIL_PAGE_TYPE
    ], get(action, 'data.page_data.page_type'))
      ? state
      : shimArticle(action);

export default (state: ArticleState = INITIAL_STATE, action: Action): ArticleState => {
  switch (action.type) {
    case RECEIVE_FOUNDRY_RESPONSE:
      return getArticleOrInitialState(state, action);
    default:
      return state;
  }
};

function shimArticle(action: Action): ArticleState {
  const [article] = get(action, 'data.content.entries', []);
  if (isEmpty(article)) {
    return {};
  }
  const { page_type } = get(action, 'data.page_data', {});
  const extractedArticle = extract(article, ARTICLE_ATTRIBUTES);
  const shimOptions = {
    sold_content_type: extractedArticle.meta.sold_content_type,
    product_layout: extractedArticle.meta.product_layout,
    page_type
  };
  const shimmedSections = {
    body: shimBodySections(extractedArticle.sections.body, shimOptions),
    slideshow: shimSlideshowSections(
      extractedArticle.sections.slideshow, shimOptions
    )
  };
  const sections = page_type === VIDEO_SERIES_PAGE_TYPE
    ? shimmedSections
    : shimAds(shimmedSections, shimOptions);

  const breadcrumbs = extractedArticle.shopping_breadcrumbs
    ? shimBreadcrumbs(extractedArticle.breadcrumbs, extractedArticle.shopping_breadcrumbs)
    : extractedArticle.breadcrumbs;

  return {
    ...extractedArticle,
    isSensitiveContent: get(action, 'data.ad_data.ad_is_sensitive_content'),
    meta: shimMeta(extractedArticle.meta),
    type: extractedArticle.type || page_type,
    sanitizedTitle: sanitizeText(extractedArticle.title),
    original_published_formatted: toFormattedDate(extractedArticle.original_published),
    published_formatted: toFormattedDate(extractedArticle.published),
    products: getProductsFromSections(sections),
    newsletters: [{
      'channel': 'default',
      'list': 'main_everywhere',
      'template': 'welcome_organic'
    }],
    breadcrumbs,
    recommendations: shimRecommendations(extractedArticle.recommendations),
    sections
  };
}

function shimMeta({ slideshow_full, full_intro_entry, ...meta }: Object): Object {
  return {
    ...meta,
    slideshow_full: slideshow_full !== '' && slideshow_full,
    full_intro_entry: !!full_intro_entry,
    product_layout: !!meta.product_layout
  };
}

function shimBreadcrumbs(breadcrumbs: Array<Object>, shoppingBreadcrumbs: Array<Object>): Array<Object> {
  const typedShoppingBreadcrumbs = map(shoppingBreadcrumbs, (breadcrumb: Object): Object => {
    return {
      ...breadcrumb,
      type: 'shopping'
    };
  });
  return [
    ...breadcrumbs,
    ...typedShoppingBreadcrumbs
  ];
}

function toFormattedDate(timestamp: number): ?string {
  if (!isInteger(timestamp)) {
    return;
  }
  return format(new Date(timestamp * 1000), TIMESTAMP_FORMAT);
}

function getProductsFromSections({ body, slideshow }: Sections): Array<Object> {
  return flatMap(
    [...body, ...slideshow],
    (section: Object): Array<Object> => {
      const products = get(section, 'content.products');
      if (!Array.isArray(products)) {
        return [];
      }
      return filter(products, 'thumbnail_url');
    }
  );
}

function shimBodySections(sections: Array<Object>, options: ShimOptions): Array<Object> {
  return shimSections([
    shimProducts,
    shimDimensions,
    shimAlignment,
    shimLegacyPolyptychs,
    shimPolyptychs,
    shimWidthPercentage,
    shimIntSections
  ], sections, options);
}

function shimSlideshowSections(sections: Array<Object>, options: ShimOptions): Array<Object> {
  return shimSections([
    shimProducts,
    shimDimensions,
    shimSlideshowAlignment,
    shimIntSections
  ], sections, options);
}

function shimSections(shimFns: Array<Function>, sections: Array<Object>, options: ShimOptions): Array<Object> {
  const callback = (sections: Array<Object>, shimFn: Function): Array<Object> =>
    shimFn(sections, options);
  return reduce(shimFns, callback, sections);
}

function shimProducts(sections: Array<Object>): Array<Object> {
  return map(sections, (section: Object): Object => {
    const hasRenderableProducts = Array.isArray(get(section, 'content.products'));
    if (isObject(section.content) && !hasRenderableProducts) {
      return {
        ...section,
        content: {
          ...section.content,
          products: []
        }
      };
    }
    return section;
  });
}

function shimDimensions(sections: Array<Object>): Array<Object> {
  return map(sections, (section: Object): Object => {
    if (!isImageSection(section)) {
      return section;
    }
    if (!isEmpty(section.content.dimensions)) {
      return section;
    }
    const { original_height_px,
      original_width_px } = section.content.preview_image;

    return {
      ...section,
      content: {
        ...section.content,
        dimensions: { original_height_px, original_width_px }
      }
    };
  });
}

/*
 * Shims empty alignments.
 * - Asset sections that aren't image sections become 'center'.
 * - Landscape image sections become 'center'.
 * - Portrait image sections become 'left';
 * Note: 'shimPolyptychs' might also alter alignments after this.
 */
function shimAlignment(sections: Array<Object>): Array<Object> {
  return map(sections, (section: Object): Object => {
    if (!shouldShimAlignment(section)) {
      return section;
    }

    if (!isImageSection(section)) {
      return {
        ...section,
        content: {
          ...section.content,
          alignment: 'center'
        }
      };
    }

    const { dimensions } = section.content;
    const { original_width_px, original_height_px } = dimensions;
    const isLandscape = original_width_px > original_height_px;

    return {
      ...section,
      content: {
        ...section.content,
        alignment: isLandscape ? 'center' : 'left'
      }
    };
  });
}

function shimSlideshowAlignment(sections: Array<Object>): Array<Object> {
  return map(sections, (section: Object): Object => ({
    ...section,
    content: {
      ...section.content,
      alignment: 'center'
    }
  }));
}

function shimLegacyPolyptychs(sections: Array<Object>): Array<Object> {
  const shimmedSections = [];

  for (let i = 0; i < sections.length;) {
    const [
      currentSection, nextSection, nextNextSection
    ] = sections.slice(i, i + 3);

    // Triptych
    if (
      currentSection && nextSection && nextNextSection
      &&
      isPolyptychFromOldStoryEditor(
        [currentSection, nextSection, nextNextSection]
      )
    ) {
      shimmedSections.push(
        {
          ...currentSection,
          content: {
            ...currentSection.content,
            alignment: 'left',
            width_percentage: 'THIRD'
          }
        },
        {
          ...nextSection,
          content: {
            ...nextSection.content,
            alignment: 'center',
            width_percentage: 'THIRD'
          }
        },
        {
          ...nextNextSection,
          content: {
            ...nextNextSection.content,
            alignment: 'right',
            width_percentage: 'THIRD'
          }
        }
      );
      i += 3;
      continue;
    }

    // Diptych
    if (
      currentSection && nextSection
      &&
      isPolyptychFromOldStoryEditor([currentSection, nextSection])
    ) {
      shimmedSections.push(
        {
          ...currentSection,
          content: {
            ...currentSection.content,
            alignment: 'left',
            width_percentage: 'HALF'
          }
        },
        {
          ...nextSection,
          content: {
            ...nextSection.content,
            alignment: 'right',
            width_percentage: 'HALF'
          }
        }
      );
      i += 2;
      continue;
    }

    shimmedSections.push(sections[i]);
    i++;
  }
  return shimmedSections;
}

/*
 * This function does a few things:
 * 1. For the middle section in a triptych, add a flag to ensure we don't
 *    break up the triptych with an ad, and to aid rendering in the
 *    ImageSection component.
 * 2. For a diptych or triptych, put all products in the last section. Also,
 *    add an attribute to help render the product carousel with the right width.
 * 3. For any image section that's NOT in a polyptych, and that DOES have
 *    products, when the product layout is off, force its alignment to 'center'
 *
 * While these functions could probably be extracted into separate shims,
 * we're keeping them in this one loop for efficiency.
 */
function shimPolyptychs(
  sections: Array<Object>, { product_layout }: ShimOptions
): Array<Object> {
  const shimmedSections = [];

  for (let i = 0; i < sections.length;) {
    const [
      currentSection, nextSection, nextNextSection
    ] = sections.slice(i, i + 3);

    // Triptych
    if (
      currentSection && nextSection && nextNextSection
      &&
      isPolyptychFromNewStoryEditor([
        currentSection, nextSection, nextNextSection
      ])
    ) {
      shimmedSections.push(
        {
          ...currentSection,
          content: {
            ...currentSection.content,
            products: []
          },
          polyptychAttributes: {
            isMiddleOfTriptych: false,
            isLastInPolyptych: false
          }
        },
        {
          ...nextSection,
          content: {
            ...nextSection.content,
            products: []
          },
          polyptychAttributes: {
            isMiddleOfTriptych: true,
            isLastInPolyptych: false
          }
        },
        {
          ...nextNextSection,
          content: {
            ...nextNextSection.content,
            products: [
              ...currentSection.content.products,
              ...nextSection.content.products,
              ...nextNextSection.content.products
            ]
          },
          polyptychAttributes: {
            isMiddleOfTriptych: false,
            isLastInPolyptych: true
          }
        }
      );
      i += 3;
      continue;
    }

    // Diptych
    if (
      currentSection && nextSection
      &&
      isPolyptychFromNewStoryEditor([currentSection, nextSection])
    ) {
      shimmedSections.push(
        {
          ...currentSection,
          content: {
            ...currentSection.content,
            products: []
          },
          polyptychAttributes: {
            isMiddleOfTriptych: false,
            isLastInPolyptych: false
          }
        },
        {
          ...nextSection,
          content: {
            ...nextSection.content,
            products: [
              ...currentSection.content.products,
              ...nextSection.content.products
            ]
          },
          polyptychAttributes: {
            isMiddleOfTriptych: false,
            isLastInPolyptych: true
          }
        }
      );
      i += 2;
      continue;
    }

    // Non-polyptych image section with products, when the product layout is off
    if (
      !product_layout
      && isImageSection(currentSection)
      && currentSection.content.products.length > 0
    ) {
      shimmedSections.push({
        ...currentSection,
        content: {
          ...currentSection.content,
          alignment: 'center'
        }
      });
      i++;
      continue;
    }

    shimmedSections.push(sections[i]);
    i++;
  }
  return shimmedSections;
}

const isPageWidth =
  ({ content: { dimensions: { original_width_px } } }: Object): bool =>
    original_width_px >= ARTICLE_PAGE_WIDTH_IMAGE_SHIM_THRESHOLD_PX;

/*
 * TODO: remove this after the New Story Editor hard launch.
 *
 * Not specifying a width_percentage (which is always the case for
 * Old Story Editor) while also floating a JW section can cause it
 * to disappear completely. Bad! Let's make sure Old Story Editor
 * non-image asset sections are always FULL.
 */
const shimWidthPercentageIfNeeded =
  (section: Object, page_type: string): Object => {
    if (!isAssetSection(section) || !wasCreatedInOldStoryEditor(section)) {
      return section;
    }
    if (!isImageSection(section)) {
      return {
        ...section,
        content: {
          ...get(section, 'content'),
          width_percentage: 'FULL'
        }
      };
    } else if (
      page_type === ARTICLE_FULL_WIDTH_PAGE_TYPE
      && isPageWidth(section)
    ) {
      return {
        ...section,
        content: {
          ...get(section, 'content'),
          width_percentage: 'PAGE'
        }
      };
    }
    return section;
  };

function shimWidthPercentage(sections: Array<Object>, { page_type }: ShimOptions): Array<Object> {
  return map(sections, (section: Object): Object =>
    shimWidthPercentageIfNeeded(section, page_type)
  );
}

/*
 * Some text sections have a `content` field that incorrectly comes back as an int.
 * Same with some pull quote sections and their `widget:content` fields.
 * Let's make sure to cast these to string.
 */
function shimIntSections(sections: Array<Object>): Array<Object> {
  return map(sections, (section: Object): Object => {
    if (typeof section.content === 'number') {
      return { ...section, content: section.content.toString() };
    }

    if (
      get(section, 'content.resource')
      && typeof section.content.resource === 'number'
    ) {
      return {
        ...section,
        content: {
          ...section.content,
          resource: section.content.resource.toString()
        }
      };
    }

    return section;
  });
}


/*
 * Floated images are displayed alongside text sections on the desktop and
 * tablet breakpoints. We avoid displaying ads after these sections to keep
 * from breaking the design flow of the article. This is enforced by
 * skipping over floated image sections when determining the cadence of ads
 * between sections.
 *
 * We also skip over sections in the middle of a triptych.
 */
function shimAds({ body, slideshow }: Sections, { sold_content_type }: ShimOptions): Sections {
  const allSections = [...body, ...slideshow];
  const bottomSection = last(allSections);
  const isPaidContent = [
    SOLD_CONTENT_TYPES.CUSTOM, SOLD_CONTENT_TYPES.SPONSORED
  ].includes(sold_content_type);
  const ads = sectionAds({ body, slideshow }, isPaidContent);
  const options = { bottomSection, isPaidContent };
  const newSections = {
    body: [],
    slideshow: []
  };
  const adPositionGenerator = standardAdPositionGenerator();

  body.forEach((section: Object, index: number): void => {
    const adPosition = getAdPosition(
      section,
      options,
      adPositionGenerator,
      ads.body[index]
    );

    newSections.body.push({ ...section, adPosition });
  });

  slideshow.forEach((section: Object, index: number): void => {
    const adPosition = getAdPosition(
      section,
      options,
      adPositionGenerator,
      ads.slideshow[index]
    );

    newSections.slideshow.push({ ...section, adPosition });
  });

  return newSections;
}

function getAdPosition(
  section: Object,
  { bottomSection, isPaidContent }: AdShimOptions,
  adPositionGenerator: Object,
  shouldDisplaySectionAd: bool
): string|null {
  if (section === bottomSection && !isPaidContent) {
    return 'bottom';
  }
  if (shouldDisplaySectionAd) {
    return adPositionGenerator.next().value;
  }
  return null;
}

function shimRecommendations(recommendations: Array<Object>): Array<Object> {
  return map(recommendations, (recommendation: Object): Object => {
    return pick(recommendation, ['id', 'title', 'url', 'opener_image']);
  });
}

type ArticleState = {};

type Sections = {
  body: Array<Object>,
  slideshow: Array<Object>
};

type AdShimOptions = {
  bottomSection: Object,
  isPaidContent: bool
};

type ShimOptions = {
  sold_content_type: bool,
  product_layout: bool,
  page_type: string
};
